import { apiBaseURL, organisationActionType, toastifyType } from "../../constants";
import apiConfig from '../../config/apiConfig';
import { addTost } from "./toastAction";

/**
 * Fetches the signed-in member's organisation context (W0): organisation,
 * membership id and the organisation ROLES that decide which workspaces the
 * sidebar offers. Personal organisations grant the full role set, so solo
 * users see both workspaces.
 */
export const fetchOrganisationMe = () => async (dispatch) => {
  try {
    const response = await apiConfig.get(apiBaseURL.ORGANISATION_ME)
    dispatch({ type: organisationActionType.FETCH_ORGANISATION_ME, payload: response.data?.data });
    return response
  } catch (error) {
    // Settled either way: the screens that wait for the context (the home route, the route
    // guard, the sidebar) must not wait for ever on a read that failed.
    dispatch({ type: organisationActionType.FETCH_ORGANISATION_ME_FAILED })
    dispatch(addTost({ type: toastifyType.SHOW_ERROR_MESSAGE, message: error?.response?.data?.message }))
  }
}

/*
 * Thin pass-through thunks for the organisation pages. Each returns the axios
 * promise untouched (no toast, no catch, no reducer dispatch) so the calling
 * page keeps full control of loading flags, error handling and result shape.
 */
const ORG = apiBaseURL.ORGANISATION

export const fetchMyOrganisationsRaw = () => () => apiConfig.get(`${ORG}/mine`)
export const fetchOrganisationMembersRaw = () => () => apiConfig.get(`${ORG}/members`)
export const fetchOrganisationInvitationsRaw = () => () => apiConfig.get(`${ORG}/invitations`)
export const acceptOrganisationInvitation = (payload) => () => apiConfig.post(`${ORG}/invitations/accept`, payload)
export const switchOrganisation = (payload) => () => apiConfig.post(`${ORG}/switch`, payload)
export const renameOrganisation = (payload) => () => apiConfig.put(ORG, payload)
export const createOrganisationInvitation = (payload) => () => apiConfig.post(`${ORG}/invitations`, payload)
export const revokeOrganisationInvitation = (invitationId) => () => apiConfig.delete(`${ORG}/invitations/${invitationId}`)
export const setOrganisationMemberStatus = (membershipId, action) => () => apiConfig.post(`${ORG}/members/${membershipId}/${action}`)
export const revokeOrganisationMemberSessions = (membershipId) => () => apiConfig.post(`${ORG}/members/${membershipId}/revoke-sessions`, {})
export const updateOrganisationMemberRoles = (membershipId, payload) => () => apiConfig.put(`${ORG}/members/${membershipId}/roles`, payload)

export const fetchOrganisationAccessScopesRaw = () => () => apiConfig.get(`${ORG}/access-scopes`)
export const fetchOrganisationMyAccessScopesRaw = () => () => apiConfig.get(`${ORG}/access-scopes/mine`)
export const createOrganisationAccessScope = (payload) => () => apiConfig.post(`${ORG}/access-scopes`, payload)
export const deleteOrganisationAccessScope = (scopeId) => () => apiConfig.delete(`${ORG}/access-scopes/${scopeId}`)

export const fetchOrganisationCreditQualityRaw = () => () => apiConfig.get(`${ORG}/credit-quality`)
export const updateOrganisationCreditQuality = (payload) => () => apiConfig.put(`${ORG}/credit-quality`, payload)

export const fetchEntitiesRaw = () => () => apiConfig.get(apiBaseURL.ENTITIES)
export const fetchPortfoliosAllRaw = () => () => apiConfig.get(`${apiBaseURL.PORTFOLIOS}/all`)

export const fetchOrganisationSsoRaw = () => () => apiConfig.get(`${ORG}/sso`)
export const createOrganisationSso = (payload) => () => apiConfig.post(`${ORG}/sso`, payload)
export const deleteOrganisationSso = (providerId) => () => apiConfig.delete(`${ORG}/sso/${providerId}`)
export const fetchOrganisationScimRaw = () => () => apiConfig.get(`${ORG}/scim`)
export const createOrganisationScimToken = () => () => apiConfig.post(`${ORG}/scim/token`, {})
export const deleteOrganisationScimToken = () => () => apiConfig.delete(`${ORG}/scim/token`)

export const fetchOrganisationPermissionsRaw = () => () => apiConfig.get(`${ORG}/permissions`)
export const updateOrganisationPermissions = (payload) => () => apiConfig.put(`${ORG}/permissions`, payload)

export const fetchOrganisationProfileRaw = () => () => apiConfig.get(`${ORG}/profile`)
export const updateOrganisationProfileRaw = (payload) => () => apiConfig.put(`${ORG}/profile`, payload)

export const fetchOrganisationSecurityRaw = () => () => apiConfig.get(`${ORG}/security`)
export const updateOrganisationSecurity = (payload) => () => apiConfig.put(`${ORG}/security`, payload)

export const fetchOrganisationSupportAccessRaw = () => () => apiConfig.get(`${ORG}/support-access`)
// `path` is built by the caller from apiBaseURL.ORGANISATION (grant decision / revoke)
export const postOrganisationPath = (path, payload) => () => apiConfig.post(path, payload)

export const fetchOrganisationTransactionCategoriesRaw = () => () => apiConfig.get(`${ORG}/transaction-categories`)
export const updateOrganisationTransactionCategories = (payload) => () => apiConfig.put(`${ORG}/transaction-categories`, payload)
